import type { Attachment } from "../proto/Attachment";
import type { MessageSignature } from "../proto/MessageSignature";
export const stores = ["openmls", "groups", "epochs", "outbox"] as const;
export type Store = typeof stores[number];
export interface Change { key: number[]; value: number[] | null }
export type Changes = Record<Store, Change[]>;
export const emptyChanges = (): Changes => ({ openmls: [], groups: [], epochs: [], outbox: [] });
export interface WasmAgent {
  execute(request: unknown): unknown;
  /** Voice agents only: queries that read the parent channel agent. */
  execute_with_parent?(request: unknown, parent: WasmAgent): unknown;
  take_changes(): Changes;
  free(): void;
  /** The device's owner pin and manager checkpoints (docs/e2e-build.md, section 6, 6b). */
  check_owner?(server: string, genesis: string, statements: string[], signatures: string[], inviteOwner?: string): OwnerCheck;
  accept_generation?(server: string, genesis: string, statements: string[], signatures: string[]): void;
  accept_managers?(server: string, anchor: Uint8Array, list: Uint8Array, now: number, revocations: Revocation[]): ManagersCheck;
  owner_view?(server: string): OwnerView | null;
  sign_manager_list?(statement: string): Uint8Array;
  next_anchor?(server: string, confirmers: string[], signedAt: number, serverAnchor: Uint8Array | undefined): string;
  /** `managers` undefined renews the server's (verified) or the accepted list. */
  next_list?(server: string, anchor: Uint8Array | undefined, managers: string[] | undefined, signedAt: number, serverAnchor: Uint8Array | undefined, serverList: Uint8Array | undefined, revocations: Revocation[]): string;
  transfer_statement?(server: string, accountKey: string, newOwner: string, serverAnchor: Uint8Array | undefined, signedAt: number): string;
}
export type OwnerCheck =
  | { kind: "pinned" } | { kind: "unchanged" } | { kind: "extended" } | { kind: "changed" }
  | { kind: "new_generation"; generation: number; owner: string };
/** `renewable`: not current, but this device may renew it (its anchor is current and names it). */
export type ManagersCheck = { kind: "accepted" | "unchanged" | "not_current" | "renewable" | "rollback" };
/** The pinned owner and the managers this device accepted. */
export interface OwnerView {
  generation: number;
  seq: number;
  head: string;
  owner: string;
  confirmers: string[];
  managers: string[];
  list_signer: string | null;
  list_signed_at: number | null;
  /** When the list signer's device certificate expires (null: a local identity, or no list). */
  list_signer_expires_at: number | null;
  anchor_seq: number | null;
  list_seq: number | null;
}
/** An owner reset, shown as a neutral notice: the device follows it only after `accept`. */
export interface OwnerGeneration { generation: number; owner: string; accept: () => Promise<void> }
export interface AgentFactory {
  create(): WasmAgent;
  load(records: Changes): WasmAgent;
}
export interface GroupSummary {
  group: number[];
  conversation: string;
  /** Decimal; 0, then one more per restart. */
  incarnation: string;
  /** The conversation's live pin: its highest accepted incarnation. */
  live: boolean;
  history: boolean;
  /** A private channel's group (6c): its access checkpoint follows every transition. */
  private: boolean;
  /** A private channel's owner at its first creation (restarts carry it on). */
  founding_owner?: string;
  /** A voice group's parent channel group, from its validated descriptor. */
  channel_group?: string;
  record: {
    /** `forgotten`: a tombstone of a deleted or hidden channel's group (pin and checkpoints only). */
    status: "creating" | "active" | "joining" | "removed" | "rejoin" | "superseded" | "forgotten";
    checkpoint: { epoch: string; hash: number[] } | null;
    server_pending: boolean;
    welcome_ids?: string[];
    own_commit_at?: number | null;
    /** Voice only: devices whose leaves the last merged commit removed. */
    voice_removed?: string[];
    /** Per uid, the epoch ranges in which it had a leaf, as this device saw them. */
    evidence?: Record<string, { first: string; last: string }[]>;
    /** A private channel's group: the newest member list this device accepted (6c). */
    access?: { generation: number; seq: number; hash: string };
    /** A tombstone of a group this device was removed from. */
    removed?: boolean;
    /** The lists of the commit that removed this device, until recorded as the checkpoint. */
    removal_access?: number[];
  };
  members: { leaf: number; device_key: string; uid: string }[];
}
export interface Gate {
  status: GroupSummary["record"]["status"];
  blocked: number[];
  server_pending: boolean;
  rejoin: boolean;
  pending_commit: boolean;
  /** A private channel's group: this device's current authority in it (6c). */
  access?: Authority;
}
/** A private group's member list as this device holds it (6c). */
export interface AccessView {
  seq: number; members: string[]; signer_uid: string; signer_device: string;
  signer_account: string | null; signer_expires_at: number | null; signed_at: number;
}
/** Current authority in a private group ((a)–(d)) and the repair this device can make otherwise. */
export type Authority = { kind: "current" } | { kind: "carry" } | { kind: "remove"; leaves: number[] } | { kind: "wait" };
/** A gate that lets this device use its group: active, nothing blocking or pending, and in a
 * private channel's group current authority. */
/** A private group this device can repair itself (carry the accepted pair, or remove leaves). */
export function repairable(gate: Gate): boolean {
  return gate.access?.kind === "carry" || gate.access?.kind === "remove";
}
export function usable(gate: Gate): boolean {
  return gate.status === "active" && !gate.rejoin && !gate.server_pending && !gate.blocked.length && !gate.pending_commit
    && (!gate.access || gate.access.kind === "current");
}
export interface CommitOutbox {
  group: number[]; epoch: string; hash: number[]; commit: number[];
  welcome: number[] | null; group_info: number[];
}
export type Resolution = "accepted" | "retry" | "rebuild" | "rejoin";
export interface Revocation { account_key: string; device_key: string; revoked_at: number }
export interface Plaintext { text: string; mentions: string[]; attachments: Attachment[]; signature: MessageSignature }
export interface SealedFields {
  conversation: string; group: string; epoch: number | string;
  message_id: string; revision: number; ciphertext: string;
}
export type OpenResult = { ok: Plaintext } | { reason: "no_key" | "cannot_open" };
/** `waiting`: a private channel whose lists give this device no current authority yet (6c). */
export type ConversationStatus = "ready" | "securing" | "creating" | "waiting";
export interface Roster {
  /** The live group; rows of earlier incarnations belong to other groups. */
  group: string;
  /** The conversation was restarted at least once. */
  restarted: boolean;
  history: boolean;
  members: GroupSummary["members"];
  /** The first epoch my account had a leaf in, as this device saw it. */
  since: number | null;
}
export interface HpkePublication { device_key: string; hpke_key: string; created_at: number; signature: string }
/** `assignment` answers the server's routing in channel groups (none in direct messages). */
export interface HistoryJob { publication: HpkePublication | null; kind: "history"; server: string; group: string; recipient: string; chunks: string[] | null; assignment?: string | null }
export interface PackageJob { kind: "packages"; server: string; packages: string[]; last_resort: string; expires_at: number }
/** A channel group joined by external commit still owes `mls.history.request`. */
export interface RequestJob { kind: "request"; server: string; group: string }
/** Where the key archive left off (docs/e2e-build.md, section 7): the newest generation it
 * accepted, the record and transfer cursors, and the generations it could open when it read
 * records (a newly opened one reads them again from the start). */
export interface ArchiveJob { kind: "archive"; newest: number; records: number; transfers: number; opened: number[] }
/** The members chosen when creating an encrypted private channel, kept until its group is settled (6c). */
export interface MembersJob { kind: "members"; server: string; conversation: string; members: string[] }
export type Job = HistoryJob | PackageJob | RequestJob | ArchiveJob | MembersJob;
/** What {@link ArchiveJob}'s agent sync reports (`ArchiveState`). */
export interface ArchiveState { newest: number | null; safe: number | null; opened: number[]; waiting: string[]; recovery_version: number | null }
export interface ArchiveUpload { generation: number; records: [string, string][]; epochs: string[] }
